import React from 'react';
import { Calendar, Clock, Archive, Trash2, RefreshCw } from 'lucide-react';
import { Card } from '../common';

// History View Component - View and load past contract reviews
const HistoryView = ({ reviews, onLoadReview, onDeleteReview, onRefresh, isLoading }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric',
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Denver'
        });
    };

    const getRetentionColor = (daysRemaining) => {
        if (daysRemaining === null || daysRemaining === undefined) return "text-gray-400";
        if (daysRemaining <= 7) return "text-red-600";
        if (daysRemaining <= 30) return "text-yellow-600";
        return "text-green-600";
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Contract Review History</h2>
                    <p className="text-sm text-gray-500 mt-1">Reviews are retained for 90 days</p>
                </div>
                <button 
                    onClick={onRefresh}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                    <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            {reviews.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                    <Archive size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No contract reviews in history</p>
                    <p className="text-sm mt-2">Completed reviews will appear here</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {reviews.map((review) => (
                        <Card key={review.review_id} className="overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-900 truncate">
                                            {review.project_name || "Untitled Review"}
                                        </h3>
                                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                            <span className="flex items-center gap-1">
                                                <Calendar size={12} />
                                                {formatDate(review.updated_at || review.created_at)}
                                            </span>
                                            <span className={`flex items-center gap-1 ${getRetentionColor(review.days_remaining)}`}>
                                                <Clock size={12} />
                                                {review.days_remaining !== null ? `${review.days_remaining} days left` : ""}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                        <button
                                            type="button"
                                            onClick={() => onLoadReview(review.review_id)}
                                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                        >
                                            Load
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log("Delete clicked for:", review.review_id);
                                                onDeleteReview(review.review_id);
                                            }}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                            title="Delete review"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Status indicators */}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {review.contract_count > 0 && (
                                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                            {review.contract_count} Contract{review.contract_count > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {review.proposal_count > 0 && (
                                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                            {review.proposal_count} Proposal{review.proposal_count > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {review.has_summary && (
                                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                            Summary
                                        </span>
                                    )}
                                    {review.has_terms && (
                                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                                            Terms
                                        </span>
                                    )}
                                    {review.has_scope && (
                                        <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                                            Scope
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default HistoryView;
